import { CropBox } from "@/store/types/crop";
import { BulkResizeEditorOperation, BulkResizeEditorOperationValue, BulkResizeImage } from "../types";

export const getCropBox = (image: BulkResizeImage, operations: BulkResizeEditorOperation[]): CropBox => {
    if (!image) {
        return {
            width: 0,
            height: 0,
            x: 0,
            y: 0,
            rotation: 0,
        };
    }

    let lastCropBox: CropBox | null = null;

    let cropBox: CropBox | null = null;

    let __hasFoundCropBox = false;

    [...operations]
        .reverse()
        .filter((o) => {
            if (o.name === "crop") {
                __hasFoundCropBox = true;
                return true;
            }

            return __hasFoundCropBox;
        })
        .reverse()
        .forEach((operation) => {
            const { name } = operation;

            if (name === "crop") {
                const value = operation.value as BulkResizeEditorOperationValue["crop"];
                if (!value.cropBox) return;

                if (cropBox) {
                    cropBox = {
                        width: value.cropBox.width,
                        height: value.cropBox.height,
                        x: cropBox.x + value.cropBox.x,
                        y: cropBox.y + value.cropBox.y,
                        rotation: value.cropBox.rotation,
                    };
                } else {
                    cropBox = value.cropBox;
                }
                return;
            }

            if (!cropBox) return;

            if (name === "flip") {
                const { value, rotation } = operation.value as BulkResizeEditorOperationValue["flip"];

                const imageWidth =
                    Math.abs(rotation) === 90 || Math.abs(rotation) === 270
                        ? image.initialDimensions.height
                        : image.initialDimensions.width;
                const imageHeight =
                    Math.abs(rotation) === 90 || Math.abs(rotation) === 270
                        ? image.initialDimensions.width
                        : image.initialDimensions.height;

                if (value === "horizontal") {
                    cropBox = {
                        ...cropBox,
                        x: imageWidth - (cropBox.x + cropBox.width),
                    };
                } else {
                    cropBox = {
                        ...cropBox,
                        y: imageHeight - (cropBox.y + cropBox.height),
                    };
                }
            } else {
                const { rotation, direction } = operation.value as BulkResizeEditorOperationValue["rotate"];

                let { x, y, width, height } = cropBox;
                let _width = width;
                width = height;
                height = _width;
                let newX = x;
                let newY = y;

                // 0 -> 90
                if (rotation === 90 && direction === "clock-wise") {
                    newY = x;
                    newX = image.initialDimensions.height - (y + cropBox.height);
                }

                // 180 -> 90
                if (rotation === 90 && direction === "counter-clock-wise") {
                    newX = y;
                    newY = image.initialDimensions.width - (x + cropBox.width);
                }

                // 90 -> 180
                if (rotation === 180 && direction === "clock-wise") {
                    newY = x;
                    newX = image.initialDimensions.width - (y + cropBox.height);
                }

                // 270 -> 180
                if (rotation === 180 && direction === "counter-clock-wise") {
                    newX = y;
                    newY = image.initialDimensions.height - (x + cropBox.width);
                }

                // 180 -> 270
                if (rotation === 270 && direction === "clock-wise") {
                    newY = x;
                    newX = image.initialDimensions.height - (y + cropBox.height);
                }

                // 270 -> 0
                if (rotation === 0 && direction === "clock-wise") {
                    newY = x;
                    newX = image.initialDimensions.width - (y + cropBox.height);
                }

                // -270 -> 0
                if (rotation === 0 && direction === "counter-clock-wise") {
                    newX = y;
                    newY = image.initialDimensions.height - (x + cropBox.width);
                }

                // 0 -> -90
                if (rotation === -90 && direction === "counter-clock-wise") {
                    newX = y;
                    newY = image.initialDimensions.width - (x + cropBox.width);
                }

                // -90 -> -180
                if (rotation === -180 && direction === "counter-clock-wise") {
                    newX = y;
                    newY = image.initialDimensions.height - (x + cropBox.width);
                }

                // -270 -> -180
                if (rotation === -180 && direction === "clock-wise") {
                    newY = x;
                    newX = image.initialDimensions.width - (y + cropBox.height);
                }

                // -180 -> -270
                if (rotation === -270 && direction === "counter-clock-wise") {
                    newX = y;
                    newY = image.initialDimensions.width - (x + cropBox.width);
                }

                // -180 -> -90
                if (rotation === -90 && direction === "clock-wise") {
                    newY = x;
                    newX = image.initialDimensions.height - (y + cropBox.height);
                }

                cropBox = {
                    width,
                    height,
                    x: newX,
                    y: newY,
                    rotation,
                };
            }
        });

    __hasFoundCropBox = false;
    [...operations]
        .reverse()
        .filter((o) => {
            if (o.name === "crop") {
                __hasFoundCropBox = true;
                return true;
            }

            return __hasFoundCropBox;
        })
        .forEach((operation) => {
            const name = operation.name;

            if (name === "crop" && !lastCropBox) {
                lastCropBox = cropBox;
            }

            if (name === "flip" && lastCropBox) {
                const { value, rotation } = operation.value as BulkResizeEditorOperationValue["flip"];

                const imageWidth =
                    Math.abs(rotation) === 90 || Math.abs(rotation) === 270
                        ? image.initialDimensions.height
                        : image.initialDimensions.width;
                const imageHeight =
                    Math.abs(rotation) === 90 || Math.abs(rotation) === 270
                        ? image.initialDimensions.width
                        : image.initialDimensions.height;

                if (value === "horizontal") {
                    lastCropBox = {
                        ...lastCropBox,
                        x: imageWidth - (lastCropBox.x + lastCropBox.width),
                    };
                } else {
                    lastCropBox = {
                        ...lastCropBox,
                        y: imageHeight - (lastCropBox.y + lastCropBox.height),
                    };
                }
            } else if (name === "rotate" && lastCropBox) {
                const { rotation, direction } = operation.value as BulkResizeEditorOperationValue["rotate"];

                let { x, y, width, height } = lastCropBox;
                let _width = width;
                width = height;
                height = _width;
                let newX = x;
                let newY = y;

                // 90 -> 0
                if (rotation === 90 && direction === "clock-wise") {
                    newX = y;
                    newY = image.initialDimensions.height - (x + lastCropBox.width);
                }

                // 90 -> 180
                if (rotation === 90 && direction === "counter-clock-wise") {
                    newX = image.initialDimensions.width - (y + lastCropBox.height);
                    newY = x;
                }

                // 180 -> 90
                if (rotation === 180 && direction === "clock-wise") {
                    newX = y;
                    newY = image.initialDimensions.width - (x + lastCropBox.width);
                }

                // 180 -> 270
                if (rotation === 180 && direction === "counter-clock-wise") {
                    newX = image.initialDimensions.height - (y + lastCropBox.height);
                    newY = x;
                }

                // 270 -> 180
                if (rotation === 270 && direction === "clock-wise") {
                    newX = y;
                    newY = image.initialDimensions.height - (x + lastCropBox.width);
                }

                // 0 -> 270
                if (rotation === 0 && direction === "clock-wise") {
                    newX = y;
                    newY = image.initialDimensions.width - (x + lastCropBox.width);
                }

                // 0 -> -270
                if (rotation === 0 && direction === "counter-clock-wise") {
                    newY = x;
                    newX = image.initialDimensions.height - (y + lastCropBox.height);
                }

                // -90 -> 0
                if (rotation === -90 && direction === "counter-clock-wise") {
                    newY = x;
                    newX = image.initialDimensions.width - (y + lastCropBox.height);
                }

                // -180 -> -90
                if (rotation === -180 && direction === "counter-clock-wise") {
                    newY = x;
                    newX = image.initialDimensions.height - (y + lastCropBox.height);
                }

                // -180 -> -270
                if (rotation === -180 && direction === "clock-wise") {
                    newX = y;
                    newY = image.initialDimensions.width - (x + lastCropBox.width);
                }

                // -270 -> -180
                if (rotation === -270 && direction === "counter-clock-wise") {
                    newY = x;
                    newX = image.initialDimensions.width - (y + lastCropBox.height);
                }

                lastCropBox = {
                    width,
                    height,
                    x: newX,
                    y: newY,
                    rotation,
                };
            }
        });

    return (
        lastCropBox ?? {
            width: 0,
            height: 0,
            x: 0,
            y: 0,
            rotation: 0,
        }
    );
};
